import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSetProduct } from "../product";
import { KnownNote, MarkKnown } from "../components/KnownProblems";
import { Trans, useTranslation } from "react-i18next";
import Scope from "../components/Scope";
import Build, { shortRef } from "../components/Build";
import { useTitle } from "../useTitle";
import { Link, useNavigate, useParams } from "react-router-dom";
import Modal from "../components/Modal";
import { trailCrumbs } from "../components/trail";
import { useAboutLine } from "../components/RunAbout";
import {
  ApiError,
  api,
  assetUrl,
  duration,
  ago,
  on,
  onDay,
  type Report,
  type Trail,
  type RunAbout,
  type Run,
  type ScenarioSummary,
  type FailureEvidence,
  type KnownProblem,
  type Comparison,
  type Step,
  atLeast,
  type Role,
} from "../api";
import i18n from "../i18n";
import { Chip, Mono, Rows, scalarOrJson } from "../components/StepParts";
import { ScenarioText, useScenarioView } from "../components/ScenarioReadable";
import type { Prose } from "../components/scenarioModel";
import { bucketOf, countIn, verdictOf, verdictWord, type Bucket } from "../components/Verdict";
import SceneResult, { isScene } from "../components/SceneResult";
import GoldenEnvironment from "../components/GoldenEnvironment";
import { FailureSummary, ReasonText } from "../components/Failure";
import { clip, formatAssert, formatInput, maskSecrets, parseReason } from "../components/humanize";
import {

  Board, Counts, Finding, INK, Loading, Notice, PageHead, Section as Part, Status, toneOfVerdict, type Count, Views,
} from "../components/Look";

/**
 * Where one of this run's cases can be read across the project's runs: the
 * scenario browser, opened on THIS run's version of the case. Null when the
 * bench is in no project. A context rather than a prop because the definition
 * block sits four components below the page, under every section.
 */
const BrowseCase = createContext<((scenario: string) => string) | null>(null);

/**
 * One run: its scenarios, and the steps behind whichever one you open.
 *
 * Failures first and expanded. The reason to open a run is almost always a
 * failure, and the current dashboard learned the same lesson - a green group is
 * not what anyone came to read.
 */
/** How long after a run finished its verdicts may still be arriving. */
export const RESULTS_GRACE_S = 3600;

/** Finished long enough ago that verdicts not here yet are not coming. */
export function verdictsLost(
  awaiting: boolean,
  finishedAt: number | null,
  now = Date.now() / 1000,
): boolean {
  return awaiting && finishedAt !== null && now - finishedAt > RESULTS_GRACE_S;
}

export default function RunDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const runId = Number(id);
  const [run, setRun] = useState<Run | null>(null);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [trail, setTrail] = useState<Trail | undefined>(undefined);
  const [about, setAbout] = useState<RunAbout | undefined>(undefined);
  const how = useAboutLine(about);
  useSetProduct(projectId);
  const [scenarios, setScenarios] = useState<ScenarioSummary[]>([]);
  const [evidence, setEvidence] = useState<Record<string, FailureEvidence>>({});
  const [known, setKnown] = useState<Record<string, KnownProblem>>({});
  const [near, setNear] = useState<Record<string, Comparison>>({});
  // Bumped after declaring a known problem, to read the run again.
  const [reread, setReread] = useState(0);
  type Plan = Record<string, { name?: string; exp?: string; klass?: string }>;
  const [groups, setGroups] = useState<{
    of: Record<string, string>;
    about: Record<string, string>;
    plan: Plan;
    tickets: { of?: Record<string, string[]>; base?: string };
  }>({ of: {}, about: {}, plan: {}, tickets: {} });
  type Definition = {
    name?: string;
    requires?: string[];
    source?: string;
    plan?: {
      group?: string;
      group_name?: string;
      objective?: string;
      preconditions?: string;
      expected_result?: string;
      priority?: string;
      tags?: string[];
    };
  };
  const [defs, setDefs] = useState<Record<string, Definition>>({});
  const [prov, setProv] = useState<Provenance>({});
  const [requested, setRequested] =
    useState<Awaited<ReturnType<typeof api.run>>["requested"]>(null);
  const [verdicts, setVerdicts] = useState<Record<string, number>>({});
  const [open, setOpen] = useState<string | null>(null);
  useTitle(
    run
      ? t("runDetail.pageTitle", { run: run.name, bench: run.bench_name })
      : t("runDetail.fallbackTitle", { id: runId }),
  );
  const [steps, setSteps] = useState<Step[]>([]);
  const [live, setLive] = useState<Step | null>(null);
  const [selftest, setSelftest] = useState<{ ok?: boolean; summary?: string; files?: string }>({});
  const [log, setLog] = useState("");
  const [pdfs, setPdfs] = useState<{ level: string; bytes: number }[]>([]);
  const [trans, setTrans] = useState<Awaited<ReturnType<typeof api.run>>["transitions"]>(null);
  const [transBuild, setTransBuild] =
    useState<Awaited<ReturnType<typeof api.run>>["transitions_build"]>(null);
  const [notCompared, setNotCompared] =
    useState<Awaited<ReturnType<typeof api.run>>["not_compared"]>(null);
  const [only, setOnly] = useState<string | null>(null);
  const [q, setQ] = useState("");
  // Null until somebody picks one: the default depends on data that arrives
  // after the first render, and a tab chosen before the counts exist would
  // land on "all" for every run and then not move.
  const [picked, setPicked] = useState<Tab | null>(null);
  const [error, setError] = useState("");
  const [role, setRole] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const d = await api.run(runId);
        if (!live) return;
        setRun(d.run);
        setProjectId(d.project_id ?? null);
        setTrail(d.trail);
        setAbout(d.about);
        setRole(d.role ?? null);
        setScenarios(d.scenarios);
        setEvidence(d.evidence ?? {});
        setKnown(d.known ?? {});
        setNear(d.near ?? {});
        setGroups(d.groups ?? { of: {}, about: {}, plan: {}, tickets: {} });
        setDefs(d.definitions ?? {});
        setProv(d.provenance ?? {});
        setRequested(d.requested ?? null);
        setVerdicts(d.verdicts || {});
        setLive(d.live ?? null);
        setSelftest(d.selftest ?? {});
        setLog(d.log ?? "");
        setTrans(d.transitions ?? null);
        setTransBuild(d.transitions_build ?? null);
        setNotCompared(d.not_compared ?? null);
        setError("");
      } catch (e) {
        if (live) setError(e instanceof ApiError ? e.message : String(e));
      }
    };
    void load();
    // Keep polling only while the run is unfinished; a finished run cannot
    // change, and polling it for ever is a tab that never goes idle.
    const t = setInterval(() => {
      if (!run?.finished_at) void load();
    }, 8000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [runId, run?.finished_at, reread]);

  useEffect(() => {
    // Reports appear a minute or so after a run closes - the bench builds them
    // one level at a time so a slow one cannot stall its other work - so this
    // is pulled with the run rather than once on mount.
    let alive = true;
    api
      .runPdfs(runId)
      .then((d) => alive && setPdfs(d.pdfs))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [runId, run?.finished_at]);

  useEffect(() => {
    if (!open) {
      setSteps([]);
      return;
    }
    let alive = true;
    const pull = () =>
      api
        .runSteps(runId, open)
        .then((r) => alive && setSteps(r.steps))
        .catch((e) => alive && setError(e instanceof ApiError ? e.message : String(e)));
    void pull();
    // The summary refreshed but the OPEN scenario did not, so watching a
    // scenario actually run showed a frozen list of its first few steps - the
    // one moment the page is worth having open.
    const t = setInterval(() => {
      if (!run?.finished_at) void pull();
    }, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [runId, open, run?.finished_at]);

  // The tiles count the WHOLE run; the sections below show what is filtered.
  // Counting the filtered list into the tiles would make the numbers move as
  // you typed, and a summary that changes with the view is not a summary.
  const inView = scenarios.filter((s) => {
    if (only && bucketOf(verdictOf(s)) !== only) return false;
    if (q.trim() && !s.scenario.toLowerCase().includes(q.trim().toLowerCase())) return false;
    return true;
  });
  // Sorted by ONE rule, the same one every count below uses: a list and the
  // number above it that disagree is the page contradicting itself.
  const inBucket = (b: Bucket) => inView.filter((s) => bucketOf(verdictOf(s)) === b);
  const failed = inBucket("failed");
  const expected = inBucket("xfailed");
  const skipped = inBucket("skipped");
  const passed = inBucket("passed");
  const count = (b: Bucket) => countIn(verdicts, scenarios, b);
  const filtering = only !== null || q.trim() !== "";
  // The WHOLE run's failures, not the filtered view: the tab label is a fact
  // about the run, and a label that moved as you typed would not be one.
  const failedAll = count("failed");
  // Failed cases that are known problems in force (verdict failed or error).
  const knownFailed = scenarios.filter(
    (s) => known[s.scenario] && ["failed", "error"].includes(verdictOf(s)),
  ).length;
  const casesAll = caseCount(verdicts, scenarios);
  const tab: Tab = picked ?? (failedAll > 0 ? "failures" : "all");
  const setTab = (t: Tab) => setPicked(t);
  // A family can be in Failed, Passed and Skipped at once, and its paragraph
  // was printed under each - the same words three times on one page. The first
  // section to carry a family describes it; the rest just list it.
  const owns = ownership([failed, expected, passed, skipped], groups.of ?? {});
  const awaitingVerdicts = scenarios.length > 0 && Object.keys(verdicts).length === 0;
  // "On their way" is true for minutes, not for ever: the agent sends the
  // verdicts when it closes the run. run40 (displayos-headless, 16 Sep 2026)
  // still said so twelve days later - its display went away mid-run and the
  // results were never produced. Past the grace the page says they are lost.
  const resultsLost = verdictsLost(awaitingVerdicts, run?.finished_at ?? null);

  const browse = useMemo(
    () =>
      projectId
        ? (scenario: string) =>
            `/projects/${projectId}/scenarios?open=${encodeURIComponent(scenario)}&run=${runId}`
        : null,
    [projectId, runId],
  );

  const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);
  // What was tested, then who asked, when and for how long - as sentences, not
  // a string of middle dots (STYLE_GUIDE.md section 2).
  const tested = run
    ? [prov.profile ? prov.profile.label || prov.profile.id : null,
       prov.build ? t("runDetail.buildNamed", { build: prov.build }) : null].filter(Boolean).join(", ")
    : "";
  const meta = run
    ? [requested ? t("runDetail.askedBy", { who: requested.requested_by }) : null,
       on(run.started_at),
       run.finished_at ? t("runDetail.took", { duration: duration(run.started_at, run.finished_at) }) : null,
       t("run.steps", { count: run.step_count }),
       run.finished_at && run.outcome && !["finished", "passed"].includes(run.outcome) ? run.outcome : null,
      ].filter(Boolean).join(", ")
    : "";
  // The counts that were tiles: each is its number and its word, and the ones
  // that filter the lists below are buttons that say whether they are on.
  const filterBy = (b: Bucket, label: string, tone: Count["tone"]): Count => ({
    n: count(b), label, tone, active: only === b,
    onClick: () => setOnly(only === b ? null : b),
    title: only === b ? t("runDetail.tileShowAll") : t("runDetail.tileShowOnly", { label }),
  });
  const counts: Count[] = run
    ? [
        filterBy("failed", t("verdict.failed", { count: failedAll }), "bad"),
        ...(count("xfailed") ? [filterBy("xfailed", t("verdict.xfailed", { count: count("xfailed") }), "warn")] : []),
        ...(count("passed") ? [filterBy("passed", t("verdict.passed", { count: count("passed") }), "good")] : []),
        ...(count("skipped") ? [filterBy("skipped", t("verdict.naHere", { count: count("skipped") }), "off")] : []),
        { n: run.step_count, label: t("run.steps", { count: run.step_count }), tone: "plain" },
      ]
    : [];

  return (
    <BrowseCase.Provider value={browse}>
    <section>
      {error && <Notice tone="error">{error}</Notice>}

      {!run && !error && <Loading label={t("common.loading")} />}
      {run && (
        <>
          <PageHead
            back={{ to: "/runs", label: t("runDetail.back") }}
            crumbs={(() => {
              const up = trailCrumbs(t, trail, "campaign");
              if (!up.length) return undefined;
              // A run outside any build or campaign still sits in its product.
              const lead = up.length === 1 ? [...up, { label: t("nav.runs"), to: "/runs" }] : up;
              const stop = trail?.campaign?.stop;
              return [...lead, { label: stop ? `${stop} · ${run.name}` : run.name }];
            })()}
            title={
              <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="font-semibold text-slate-900 dark:text-slate-100">{run.name}</span>
                <span className="text-slate-500 dark:text-slate-400">
                  {t("runDetail.onBench", { bench: run.bench_name })}
                </span>
                <Scope run={run} />
              </span>
            }
            answer={
              run.finished_at === null
                ? t("runDetail.answerRunning")
                : resultsLost
                  ? t("runDetail.answerLost")
                  : awaitingVerdicts
                  ? t("runDetail.answerAwaiting")
                  : <Answer failed={failedAll} known={knownFailed} />
            }
            bad={resultsLost || (run.finished_at !== null && !awaitingVerdicts && failedAll > knownFailed)}
            after={
              <>
                {/* WHY, in the words of whoever started it: what they wanted to
                    know is the first thing a reader of the result needs. */}
                {requested?.reason && (
                  <p className="text-slate-800 dark:text-slate-200" data-testid="run-why">{requested.reason}</p>
                )}
                {tested && <p>{cap(tested)}.</p>}
                {/* How it tested, in words: "replay" alone told nobody that
                    this is recorded vehicle traffic played back. */}
                {how && <p data-testid="run-how">{how}</p>}
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {cap(meta)}.
                  {requested?.campaign_id ? (
                    <>
                      {" "}
                      <Link className="underline" to={`/campaigns/${requested.campaign_id}`}>
                        {t("runDetail.partOfCampaign")}
                      </Link>
                    </>
                  ) : null}
                </p>
              </>
            }
          />

          {live && <NowRunning st={live} />}
          {casesAll > 0 && <div className="mt-2"><Counts items={counts} /></div>}
          <MovedSince prov={prov} />
          {/* A harness that failed its own gate is a finding, outside Details. */}
          {selftest.ok === false && <SelfTest st={selftest} />}

          <Changed t={trans} />

          {/* DETAILS, folded: the tags, checksums and self-test line explain a
              result after the fact; they are not what the page is for. Nothing
              is removed (STYLE_GUIDE.md section 1). */}
          <details className="mt-4 text-sm" data-testid="run-details">
            <summary className="cursor-pointer text-slate-500 dark:text-slate-400">{t("runDetail.details")}</summary>
            <div className="mt-2 space-y-2">
              {/* WHY NOTHING IS COMPARED, and the second baseline, folded here.
                  Both are about a comparison that did not happen or that nobody
                  asked for: 29 words explaining an absence, and a whole second
                  panel in the same grammar as the first. The comparison a
                  reader came for stays above. */}
              <NotCompared note={trans ? null : notCompared} />
              <Changed
                t={transBuild}
                against={t("runDetail.sameVariantBuild", {
                  variant:
                    transBuild?.baseline.variant || t("runDetail.variantFallback"),
                  from: shortRef(transBuild?.baseline.build_ref ?? ""),
                  to: shortRef(prov.build_ref ?? ""),
                })}
              />
              <Provenance prov={prov} />
              {selftest.ok === true && <SelfTest st={selftest} />}
              {/* OFFERED ONLY TO SOMEBODY WHO MAY DO IT. This was the one
                  destructive control on a read-side page with no role check at
                  all: every reader saw it and the server answered 403 on the
                  click. Deleting a run needs `configure` on its bench, which is
                  what the DELETE route checks, so the page asks the same
                  question with the role the server just sent. */}
              {run.finished_at !== null && atLeast(role, "maintainer") && (
                <button type="button" onClick={() => setDeleting(true)} className="btn-danger">
                  {t("runDetail.deleteThisRun")}
                </button>
              )}
            </div>
          </details>
          {deleting && (
            <DeleteRun
              run={run}
              onClose={() => setDeleting(false)}
              onDeleted={() => nav("/runs")}
            />
          )}

          {awaitingVerdicts && (
            <Notice tone="warn">
              <Trans
                i18nKey={resultsLost ? "runDetail.lostVerdicts" : "runDetail.awaitingVerdicts"}
                components={{ b: <b /> }}
              />
            </Notice>
          )}

          {/* THE VIEWS, AND WHY THIS PAGE NEEDED THEM. run114 measured 3 538 px:
              four sections stacked, all open, with 204 passing cases between the
              four failures and the report buttons. The reason to open a run is
              almost always a failure. Every case is still one click away under
              All cases, the same list in the same order. */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Tabs
              tab={tab}
              setTab={setTab}
              counts={{ failures: failedAll, all: casesAll }}
              hasLog={log.trim() !== ""}
            />
            <input
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900 sm:ml-auto sm:w-72"
              placeholder={t("runDetail.filterPlaceholder")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label={t("runDetail.filterLabel")}
            />
          </div>

          {filtering && (
            <p className="mt-3 text-sm text-slate-500">
              {t("runDetail.showing", {
                shown: inView.length,
                total: scenarios.length,
              })}{" "}
              <button
                type="button"
                className="underline"
                onClick={() => {
                  setOnly(null);
                  setQ("");
                }}
              >
                {t("runDetail.showEverything")}
              </button>
            </p>
          )}

          {tab === "failures" && (
            failed.length > 0 ? (
              <CaseSection runId={runId} groups={groups} defs={defs}
                           title=""
                           rows={failed} open={open} setOpen={setOpen} steps={steps} near={near} evidence={evidence}
                           known={known} projectId={projectId} mayDeclare={atLeast(role, "maintainer")}
                           onKnown={() => setReread((n) => n + 1)}
                           describe={owns[0]} />
            ) : (
              <div className="mt-6">
                <Notice>
                  {filtering
                    ? t("runDetail.noFailuresMatch")
                    : t("runDetail.nothingFailed")}
                </Notice>
              </div>
            )
          )}

          {tab === "all" && (
            <>
              {failed.length > 0 && (
                <CaseSection runId={runId} groups={groups} defs={defs} title={t("runDetail.sectionFailedN", { count: failed.length })}
                             rows={failed} open={open} setOpen={setOpen} steps={steps} near={near} evidence={evidence}
                           known={known} projectId={projectId} mayDeclare={atLeast(role, "maintainer")}
                           onKnown={() => setReread((n) => n + 1)}
                             describe={owns[0]} />
              )}
              {expected.length > 0 && (
                <CaseSection
                  runId={runId}
                  defs={defs}
                  groups={groups}
                  title={t("runDetail.sectionExpected", { count: expected.length })}
                  rows={expected} open={open} setOpen={setOpen} steps={steps} near={near}
                  describe={owns[1]}
                />
              )}
              {passed.length > 0 && (
                <CaseSection runId={runId} groups={groups} defs={defs} title={t("runDetail.sectionPassed", { count: passed.length })} rows={passed}
                             open={open} setOpen={setOpen} steps={steps} near={near} describe={owns[2]} />
              )}
              {skipped.length > 0 && (
                <CaseSection runId={runId} groups={groups} defs={defs} title={t("runDetail.sectionSkipped", { count: skipped.length })} rows={skipped}
                             open={open} setOpen={setOpen} steps={steps} near={near} describe={owns[3]} />
              )}
              {inView.length === 0 && (
                <div className="mt-6">
                  <Notice>
                    {filtering
                      ? t("runDetail.nothingMatches")
                      : t("runDetail.noCases")}
                  </Notice>
                </div>
              )}
            </>
          )}

          {tab === "report" && <Reports runId={runId} pdfs={pdfs} />}

          {tab === "log" && <RunLog text={log} bare />}
        </>
      )}
    </section>
    </BrowseCase.Provider>
  );
}

function CaseSection({
  title,
  rows,
  open,
  setOpen,
  steps,
  runId,
  groups,
  defs,
  describe,
  evidence,
  known,
  projectId,
  mayDeclare,
  onKnown,
  near,
}: {
  near?: Record<string, Comparison>;
  evidence?: Record<string, FailureEvidence>;
  known?: Record<string, KnownProblem>;
  projectId?: number | null;
  mayDeclare?: boolean;
  onKnown?: () => void;
  /** The families whose description THIS section prints. A family can appear
      in Failed, Passed and Skipped at once, and printing its paragraph in each
      put the same words on the page three times. */
  describe?: Set<string>;
  title: string;
  rows: ScenarioSummary[];
  open: string | null;
  setOpen: (s: string | null) => void;
  steps: Step[];
  runId: number;
  defs?: Record<string, ScenarioDefinition>;
  groups: {
    of: Record<string, string>;
    about: Record<string, string>;
    plan: Record<string, { name?: string; exp?: string; klass?: string }>;
    tickets?: { of?: Record<string, string[]>; base?: string };
  };
}) {
  return (
    <Part title={title}>
      <List
        runId={runId}
        groups={groups}
        defs={defs}
        rows={rows}
        open={open}
        setOpen={setOpen}
        steps={steps}
        describe={describe}
        evidence={evidence}
        near={near}
        known={known}
        projectId={projectId}
        mayDeclare={mayDeclare}
        onKnown={onKnown}
      />
    </Part>
  );
}

export type Provenance = {
  harness?: string;
  product_data?: string;
  product?: string;
  build?: string;
  build_ref?: string;
  build_recorded?: boolean;
  variant?: string;
  variant_label?: string;
  /** Which execution profile ran, and the fingerprint of the test data it was
   *  prepared with. Absent where the bench prepares no profiles. */
  profile?: { id: string; label: string; test_data: string; mode: string };
  since?: {
    run_id: number;
    run: string;
    harness_changed: boolean;
    product_data_changed: boolean;
    /** Three-valued: null means one of the two runs did not say, which must
     *  not be shown as "no change". */
    build_changed?: boolean | null;
    /** `run` is the last run of the SAME profile, not merely the last run on
     *  the bench - which on a bench that alternates profiles is another recipe. */
    same_profile?: boolean;
    /** Three-valued like build_changed. */
    test_data_changed?: boolean | null;
  };
};

/** Which harness and which goldens produced this run.

    A bench runs a COPY of both, and a stale or duplicated copy does not
    announce itself: it produces red cases that read exactly like a product
    regression, and the first hour of the investigation goes to the wrong
    suspect. Two digests and one sentence - "the product data changed since
    run N" - turn that into something a reader sees before they start.

    Stated, not judged. A changed digest is usually a deploy, which is how work
    gets done; it is only evidence, and it is only interesting beside a result
    that also changed. */
export function Provenance({ prov }: { prov: Provenance }) {
  const { t } = useTranslation();
  if (!prov.harness && !prov.product_data && prov.build_recorded === undefined) return null;
  const s = prov.since;
  const moved = s && (s.harness_changed || s.product_data_changed);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
      {/* THE PRODUCT, beside the two digests that describe the things testing
          it. "the product changed" and "the test data changed" are different
          sentences with different consequences, and until now only the second
          could be said - the run recorded no build at all. */}
      {prov.build_recorded !== undefined && (
        <Build build={prov.build} ref_={prov.build_ref} recorded={prov.build_recorded}
               variant={prov.variant} variantLabel={prov.variant_label} />
      )}
      {s?.build_changed === true && (
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
          <Trans
            i18nKey="runDetail.buildChangedSince"
            values={{ run: s.run }}
            components={{
              runLink: <Link to={`/runs/${s.run_id}`} className="underline" />,
            }}
          />
        </span>
      )}
      {prov.harness && (
        <span className="font-mono" title={t("runDetail.harnessDigestTitle")}>
          {t("runDetail.harness", { digest: prov.harness })}
        </span>
      )}
      {prov.product_data && (
        <span
          className="font-mono"
          title={t("runDetail.productDataTitle")}
        >
          {t("runDetail.productData", {
            product: prov.product || t("runDetail.productFallback"),
            digest: prov.product_data,
          })}
        </span>
      )}
      {prov.profile?.test_data && (
        <span className="font-mono" title={t("runDetail.testDataTitle")}>
          {t("runDetail.testData", { digest: prov.profile.test_data.slice(0, 12) })}
        </span>
      )}
      {moved && (
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
          <Trans
            i18nKey="runDetail.movedSince"
            values={{
              what: [
                s.harness_changed ? t("runDetail.movedHarness") : null,
                s.product_data_changed ? t("runDetail.movedProductData") : null,
              ]
                .filter(Boolean)
                .join(t("runDetail.movedJoin")),
              run: s.run,
            }}
            components={{
              runLink: <Link to={`/runs/${s.run_id}`} className="underline" />,
            }}
          />
        </span>
      )}
    </div>
  );
}

/** Deleting a run, with the name typed out.

    Offered quietly - small, grey, under the header - because it is the one
    control on this page that destroys something, and it is wanted rarely: an
    experiment published by mistake, a run from a bench being set up. An id is a
    number in a URL and the two runs anybody deletes are the junk one and the
    one below it in the list, so the name is the confirmation, exactly as it is
    for deleting a bench.

    The button appears only on a FINISHED run. Deleting one an agent is still
    publishing into does not stop the agent: its next batch 404s and `runs/open`
    hands it a new empty run under the same name, so the record returns half
    missing and looks like data loss. The service refuses that too - this only
    keeps the reader from meeting the refusal. */
export function DeleteRun({
  run,
  onClose,
  onDeleted,
}: {
  run: Run;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  return (
    <Modal title={t("runDetail.deleteTitle", { name: run.name })} onClose={onClose}>
      <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">
        <Trans
          i18nKey="runDetail.deleteWarn"
          values={{ name: run.name }}
          components={{ name: <b /> }}
        />
      </p>
      <input
        autoFocus
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-800"
      />
      {err && <p className="mt-2 text-sm text-red-700 dark:text-red-400">{err}</p>}
      <div className="mt-3 flex justify-end gap-2">
        <button className="px-3 py-1.5 text-sm" onClick={onClose} disabled={busy}>
          {t("common.cancel")}
        </button>
        <button
          className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          disabled={typed !== run.name || busy}
          onClick={async () => {
            setBusy(true);
            setErr("");
            try {
              await api.deleteRun(run.id, typed);
              onDeleted();
            } catch (e) {
              setErr(e instanceof ApiError ? e.message : String(e));
              setBusy(false);
            }
          }}
        >
          {t("common.delete")}
        </button>
      </div>
    </Modal>
  );
}

/** The run's answer, in one sentence, before anything else on the page.

    STYLE_GUIDE.md section 1: the tiles below are filters and count everything
    at once, while somebody who opened a run first wants to know whether
    anything failed. The word carries the result as well as the colour. */
export function Answer({ failed, known = 0 }: { failed: number; known?: number }) {
  const { t } = useTranslation();
  // ONE SENTENCE, NOT TWO. The breakdown used to be a second line here -
  // "9 passed, 2 expected failures, 1 not run here" - built from
  // runDetail.answerPassed/answerExpected/answerNotRun, which are character
  // for character the strings the Counts tiles render a few pixels below.
  // Saying it twice made the answer longer without making it clearer, so the
  // headline keeps the verdict and the tiles keep the breakdown, where they
  // double as the filter.
  return (
    <div data-testid="run-answer">
      {/* Known problems counted apart, as on the campaign and the Overview:
          run48's three BAWU failures were all known and the page still said
          "3 tests failed." in red. The failures stay failures below. */}
      <p className={failed > known ? "text-red-700 dark:text-red-400" : undefined}>
        {!failed
          ? t("runDetail.answerNothingFailed")
          : known >= failed
            ? t("runDetail.answerAllKnown", { count: failed })
            : known
              ? `${t("runDetail.answerFailed", { count: failed - known })} ${t("known.also", { n: known })}`
              : t("runDetail.answerFailed", { count: failed })}
      </p>
    </div>
  );
}

/** What moved under this run since the one before it, as a sentence.

    The same facts the provenance tags carry, said in words where the reader
    is looking: a different build, framework or test data explains a changed
    result before anybody reads a checksum. The tags stay under Details. */
export function MovedSince({ prov }: { prov: Provenance }) {
  const { t } = useTranslation();
  const s = prov.since;
  if (!s) return null;
  const what = [
    s.build_changed === true ? t("runDetail.movedWhatBuild") : null,
    s.harness_changed ? t("runDetail.movedWhatHarness") : null,
    // The profile's data (recordings, reference pictures, the layout package)
    // and the product's tests are two different sentences with different
    // suspects, so they are said apart.
    s.test_data_changed === true ? t("runDetail.movedWhatData") : null,
    s.product_data_changed ? t("runDetail.movedWhatTests") : null,
  ]
    .filter(Boolean)
    .join(t("runDetail.movedJoin"));
  if (!what) return null;
  return (
    <p className="mt-2 text-sm text-amber-800 dark:text-amber-300" data-testid="run-moved">
      <Trans
        i18nKey={s.same_profile ? "runDetail.movedSentenceProfile" : "runDetail.movedSentence"}
        values={{ what, run: s.run }}
        components={{ runLink: <Link to={`/runs/${s.run_id}`} className="underline" /> }}
      />
    </p>
  );
}

/**
 * The family a scenario belongs to: HW-LED-008 -> HW-LED.
 *
 * DERIVED FROM THE ID, not from the plan. The plan's categories carry better
 * names and a paragraph on why each family exists, but the service has never
 * been told the plan - and grouping by id family is exactly what the bench's
 * own page falls back to for a test the plan does not list. Better a real
 * grouping now than none until the plan is uploaded.
 */

/**
 * Why the case failed, on the row, in words.
 *
 * The suite's own assertion - `AssertionError: step 2 (['wait_for']): rc 1
 * not in [0] (stderr='condition not met after 12s ...')` - was shown as it
 * came, in red monospace, and on a phone it read as a wall of punctuation cut
 * off mid-word. It is parsed now (components/humanize.ts, by the harness's own
 * grammar) into the step, the action and a sentence; the raw text stays in
 * `title` for a mouse and whole in the step record below.
 *
 * Clamped to two lines INSIDE the padding: a clamp on the padded box let the
 * top of the third line show through the bottom padding, sliced in half.
 */
function Why({ message, verdict }: { message?: string; verdict: string }) {
  const failed = verdict === "failed" || verdict === "error";
  const text = (message ?? "").trim();
  if (!failed || !text) return null;
  return (
    <div title={maskSecrets(text)} className="-mt-1 px-4 pb-3">
      <Finding>
        <p className="line-clamp-2 break-words leading-snug">
          <FailureSummary message={text} />
        </p>
      </Finding>
    </div>
  );
}


/**
 * How many cases the run has, from the VERDICT table where there is one.
 *
 * `scenarios` is derived from step rows, and a case skipped before it ran
 * anything leaves none - so counting the list labels run114 "All cases 211"
 * directly under a tile reading 273. Same number, sourced from the thing that
 * cannot know it; the tiles already carry a comment about this exact mistake,
 * and the first draft of the tab repeated it.
 */
export function caseCount(
  verdicts: Record<string, number>,
  scenarios: { verdict?: string | null; failed_checks: number }[],
): number {
  // The buckets' sum, so the total can never disagree with the tiles beside it.
  return (["failed", "xfailed", "passed", "skipped"] as const).reduce(
    (n, b) => n + countIn(verdicts, scenarios, b),
    0,
  );
}

type Tab = "failures" | "all" | "report" | "log";

/**
 * Which section prints a family's description, when several contain it.
 *
 * First one wins, in the order they are rendered. The alternative - every
 * section describing every family it holds - put the same paragraph on run114
 * three times, once under Failed, once under Passed, once under Skipped, and
 * accounted for most of that page's 881 words of prose.
 */
export function ownership(
  sections: { scenario: string }[][],
  byId: Record<string, string>,
): Set<string>[] {
  const seen = new Set<string>();
  return sections.map((rows) => {
    const mine = new Set<string>();
    for (const r of rows) {
      const g = groupKeyOf(byId, r.scenario);
      if (!seen.has(g)) {
        seen.add(g);
        mine.add(g);
      }
    }
    return mine;
  });
}

/**
 * The run's parts, as tabs.
 *
 * Counts on the labels, because a tab you have to open to find out whether it
 * is worth opening is a tab nobody opens. "Failures 0" is worth showing: it is
 * the answer, and hiding the tab would leave the reader wondering where it
 * went.
 */
function Tabs({
  tab,
  setTab,
  counts,
  hasLog,
}: {
  tab: Tab;
  setTab: (t: Tab) => void;
  counts: { failures: number; all: number };
  hasLog: boolean;
}) {
  const { t } = useTranslation();
  const items: [Tab, string][] = [
    ["failures", `${t("runDetail.tabFailures")} ${counts.failures}`],
    ["all", `${t("runDetail.tabAll")} ${counts.all}`],
    ["report", t("runDetail.tabReport")],
  ];
  if (hasLog) items.push(["log", t("runDetail.tabLog")]);
  return <Views items={items} value={tab} onChange={setTab} label={t("runDetail.tablistLabel")} />;
}

/**
 * Which group a case belongs to: the plan's own category when the bench sent
 * one, the id family otherwise.
 *
 * Exported so the page and the list cannot disagree about it. The page has to
 * know the same answer to decide which section owns a family's description,
 * and a second copy of a rule is exactly how `verdictOf` came apart.
 */
export function groupKeyOf(byId: Record<string, string>, scenario: string): string {
  return byId[scenario] || familyOf(scenario);
}

export function familyOf(scenario: string): string {
  return scenario.replace(/-\d+[a-z]?$/, "") || scenario;
}

export type ScenarioDefinition = {
  name?: string;
  requires?: string[];
  /** The step actions, in order of first use (harness 2026-09-10 on). */
  verbs?: string[];
  source?: string;
  plan?: {
    group?: string;
    group_name?: string;
    objective?: string;
    preconditions?: string;
    expected_result?: string;
    priority?: string;
    tags?: string[];
  };
};

/** What a case IS, beside what it did.

    The steps above this are the INVOCATION - `compare_golden golden=... grab=
    {...}` - and the assertions are the verdict. Neither says why the case
    exists, what a failure would mean, or which measurement produced a
    threshold. That is written down, in the comments beside the steps in
    scenarios.yaml, and until now it was legible only to somebody holding a
    checkout at the right commit; everyone else read an id and guessed.

    Collapsed by default, because the reader who opened a scenario usually came
    for the failing step. The text is the block AS WRITTEN, comments included -
    re-rendering it from parsed YAML would drop exactly the half worth
    shipping. So it is shown twice over, on tabs: in words, with each comment
    placed on the step it explains (ScenarioReadable), and as written,
    coloured but not rewritten (YamlSource). */
function Definition({ def, scenario }: { def?: ScenarioDefinition; scenario: string }) {
  const { t } = useTranslation();
  const browse = useContext(BrowseCase);
  if (!def || (!def.source && !def.plan && !def.requires?.length)) return null;
  const p = def.plan;
  return (
    <details className="border-t border-slate-200 bg-slate-50/60 px-3 py-2 text-xs dark:border-slate-800 dark:bg-slate-800/30">
      <summary className="cursor-pointer text-slate-500 dark:text-slate-400">
        {t("runDetail.definition")}
        {p?.group_name ? (
          <span className="ml-2 text-slate-400">
            {p.group}
            {p.group ? " · " : ""}
            {p.group_name}
          </span>
        ) : null}
      </summary>
      <div className="mt-2 space-y-1">
        {def.requires?.length ? (
          <div className="flex flex-wrap items-center gap-1">
            <span className="font-medium text-slate-500">
              {t("runDetail.requires")}
            </span>
            {def.requires.map((r) => (
              <Chip key={r}>{r}</Chip>
            ))}
          </div>
        ) : null}
        {p?.preconditions && (
          <div className="text-slate-600 dark:text-slate-400">
            <span className="font-medium text-slate-500">
              {t("runDetail.preconditions")}
            </span>{" "}
            {p.preconditions}
          </div>
        )}
        {p?.expected_result && (
          <div className="text-slate-600 dark:text-slate-400">
            <span className="font-medium text-slate-500">
              {t("runDetail.expected")}
            </span>{" "}
            {p.expected_result}
          </div>
        )}
        {def.source && (
          <ScenarioText
            source={def.source}
            verbs={def.verbs}
            sourceClassName="mt-1 max-h-96 overflow-auto whitespace-pre rounded bg-white p-2 font-mono text-[11px] leading-snug text-slate-700 dark:bg-slate-900 dark:text-slate-300"
          />
        )}
        {browse && (
          <Link className="inline-block text-slate-500 underline dark:text-slate-400" to={browse(scenario)}>
            {t("runDetail.browseCase")}
          </Link>
        )}
      </div>
    </details>
  );
}

export function List({
  rows,
  open,
  setOpen,
  steps,
  runId,
  groups,
  defs = {},
  describe,
  evidence = {},
  near = {},
  known = {},
  projectId = null,
  mayDeclare = false,
  onKnown,
}: {
  /** The failed step of each failed case, to show its picture on the row. */
  evidence?: Record<string, FailureEvidence>;
  /** Picture comparisons that passed with little to spare, by case. */
  near?: Record<string, Comparison>;
  /** Known problems in force, by case: said on the row they explain. */
  known?: Record<string, KnownProblem>;
  projectId?: number | null;
  /** Whether this reader may declare a failure a known problem (maintainer). */
  mayDeclare?: boolean;
  onKnown?: () => void;
  /** The families whose description this list prints. Absent means all of
      them, which is what a list rendered on its own wants. */
  describe?: Set<string>;
  rows: ScenarioSummary[];
  open: string | null;
  setOpen: (s: string | null) => void;
  steps: Step[];
  runId: number;
  defs?: Record<string, ScenarioDefinition>;
  groups: {
    of: Record<string, string>;
    about: Record<string, string>;
    plan: Record<string, { name?: string; exp?: string; klass?: string }>;
    tickets?: { of?: Record<string, string[]>; base?: string };
  };
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  if (rows.length === 0) {
    return <p className="mt-2 text-sm text-slate-500">{t("runDetail.none")}</p>;
  }

  const about = groups.about ?? {};
  const byId = groups.of ?? {};
  const buckets = new Map<string, ScenarioSummary[]>();
  for (const s of rows) {
    // The plan's own category when the bench sent one - those carry the real
    // names and the paragraph explaining what the family is for. The id family
    // is the fallback, not the intent.
    const g = groupKeyOf(byId, s.scenario);
    (buckets.get(g) ?? buckets.set(g, []).get(g)!).push(s);
  }
  const names = [...buckets.keys()].sort();
  // The same reason under every known failure was the same sentence three
  // times on run48. Said once above the list when all of them share it; each
  // row then says only that it is known, and until when.
  const knownRows = rows.filter((s) => {
    const v = verdictOf(s);
    return (v === "failed" || v === "error") && known[s.scenario];
  });
  const shared =
    knownRows.length > 1 && new Set(knownRows.map((s) => known[s.scenario].reason)).size === 1
      ? known[knownRows[0].scenario]
      : null;
  const caseLink = (id: string) =>
    projectId ? (
      <Link className="whitespace-nowrap text-sm underline" to={`/projects/${projectId}/cases/${encodeURIComponent(id)}`}>
        {t("runDetail.casePage")}
      </Link>
    ) : null;

  return (
    <div className="space-y-3">
      {shared && (
        <p className="text-base text-amber-800 dark:text-amber-300" data-testid="known-shared">
          {t("known.shared", { count: knownRows.length, reason: shared.reason })}
          {shared.created_by && <span className="text-sm text-slate-500"> · {t("known.by", { who: shared.created_by })}</span>}
        </p>
      )}
      {names.map((g) => {
        const items = buckets.get(g)!;
        // The whole group's result, not just its failures. "12 tests, 1 failed"
        // leaves the other eleven unexplained - passed, skipped for a missing
        // capability, and drafts expected to fail are three different things,
        // and which one a group is full of decides whether its failure matters.
        const tally = { passed: 0, failed: 0, skipped: 0, xfailed: 0 };
        for (const s of items) {
          const v = verdictOf(s);
          tally[bucketOf(v)]++;
        }
        const bad = tally.failed;
        // A green group is not what anyone opened the page for, so it starts
        // shut. Anything with a failure in it starts open - a finding behind a
        // chevron is a finding nobody reads.
        const shut = collapsed[g] ?? bad === 0;
        return (
          <Board key={g}>
            <button
              type="button"
              className="flex w-full flex-wrap items-baseline gap-x-3 gap-y-1 bg-slate-50 px-4 py-2 text-left hover:bg-slate-100 dark:bg-slate-950/40 dark:hover:bg-slate-800"
              onClick={() => setCollapsed({ ...collapsed, [g]: !shut })}
              aria-expanded={!shut}
            >
              <span className="text-slate-400" aria-hidden="true">{shut ? "▸" : "▾"}</span>
              {/* A group is named by the plan, in words - not an identifier. */}
              <span className="min-w-0 text-sm font-semibold text-slate-800 dark:text-slate-200">{g}</span>
              {/* Counts wrap as WHOLE pieces. Squeezed beside a long name on a
                  phone they broke into columns - "2 / testy", "2 /
                  niepowodzenia". */}
              <span className="whitespace-nowrap text-sm text-slate-500">
                {t("runDetail.groupTests", { count: items.length })}
              </span>
              <span className="ml-auto flex flex-wrap items-baseline gap-x-3 whitespace-nowrap text-sm">
                {tally.failed > 0 && (
                  <span className={`font-semibold ${INK.bad}`}>{t("verdict.failed", { count: tally.failed })}</span>
                )}
                {tally.passed > 0 && (
                  <span className={INK.good}>{t("verdict.passed", { count: tally.passed })}</span>
                )}
                {tally.xfailed > 0 && (
                  <span className={INK.warn}>{t("verdict.xfailed", { count: tally.xfailed })}</span>
                )}
                {tally.skipped > 0 && (
                  <span className={INK.off}>{t("runDetail.tallySkipped", { count: tally.skipped })}</span>
                )}
              </span>
            </button>
            {/* What the family is FOR. A reader who has never seen this suite
                cannot tell whether a group's failures matter to them from the
                id alone - which is why the bench's page carries this prose. */}
            {about[g] && (!describe || describe.has(g)) && (
              <p className="bg-slate-50/60 px-4 py-2 text-sm text-slate-500 dark:bg-slate-950/20 dark:text-slate-400">
                {about[g]}
              </p>
            )}
            {!shut && items.map((s) => {
              // Through verdictOf, like the grouping and the tally: three
              // spellings of one rule is how the other two came apart.
              const v = verdictOf(s);
              return (
                <div key={s.scenario}>
                  <button
                    type="button"
                    aria-expanded={open === s.scenario}
                    className="grid w-full gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 sm:grid-cols-[10rem_minmax(0,1fr)_auto] sm:items-baseline"
                    onClick={() => setOpen(open === s.scenario ? null : s.scenario)}
                  >
                    {(v === "failed" || v === "error") && known[s.scenario] ? (
                      <Status tone="warn" title={verdictWord(v)}>{t("known.status")}</Status>
                    ) : (
                      <Status tone={toneOfVerdict(v)}>{verdictWord(v)}</Status>
                    )}
                    <span className="min-w-0">
                      <span className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100">{s.scenario}</span>
                      {/* What the test IS. An id is a handle, not a description,
                          and reading a run meant keeping the plan open on
                          another machine to find out what failed. */}
                      {groups.plan?.[s.scenario]?.name && (
                        <span className="ml-2 text-base text-slate-700 dark:text-slate-300">
                          {groups.plan[s.scenario].name}
                        </span>
                      )}
                      {(groups.tickets?.of?.[s.scenario] ?? []).map((k) => (
                        // Mapped by the issue's own "Test ID:" field, never by
                        // title - matching by name is how a case ends up linked
                        // to somebody else's ticket.
                        <a
                          key={k}
                          href={`${groups.tickets?.base ?? ""}/${k}`}
                          target="_blank"
                          rel="noreferrer noopener"
                          onClick={(e) => e.stopPropagation()}
                          className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600 hover:bg-sky-100 hover:text-sky-800 dark:bg-slate-800 dark:text-slate-300"
                        >
                          {k}
                        </a>
                      ))}
                    </span>
                    <span className="text-sm text-slate-500 sm:text-right">
                      {t("runDetail.caseSteps", { count: s.steps })}
                      {s.failed_checks
                        ? t("runDetail.caseFailedChecks", {
                            count: s.failed_checks,
                          })
                        : ""}
                    </span>
                  </button>
                  {/* WHY IT FAILED, WITHOUT A CLICK. `message` is the suite's own
                      assertion; the row used to say "2 steps · 1 failed check"
                      with the reason two disclosures away. Only on a failure. */}
                  {/* A picture comparison says its score instead of the exit
                      code the assertion saw; anything else keeps its message. */}
                  {!(evidence[s.scenario]?.golden && evidence[s.scenario]?.golden?.pass === false) && (
                    <Why message={s.message} verdict={v} />
                  )}
                  {/* Green today, red after the next font update: run48 missed
                      by 0.00002, and a pass by as little is the same finding a
                      build early. Said on the row, not only on the case page. */}
                  {near[s.scenario] && v !== "failed" && v !== "error" && (
                    <p className="-mt-1 px-4 pb-3 text-sm text-amber-800 dark:text-amber-300" data-testid="near-threshold">
                      {t("likeness.near", {
                        score: score(near[s.scenario].similarity),
                        threshold: score(near[s.scenario].threshold),
                        gap: score(near[s.scenario].similarity - near[s.scenario].threshold),
                      })}
                    </p>
                  )}
                  {(v === "failed" || v === "error") && (known[s.scenario] || projectId) && (
                    <div className="-mt-1 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 pb-3">
                      <div className="min-w-0 space-y-1">
                        {known[s.scenario] ? (
                          shared ? (
                            <p className="text-sm text-amber-800 dark:text-amber-300" data-testid="known-note">
                              {t("known.short", { date: onDay(known[s.scenario].expires_at) })}
                            </p>
                          ) : (
                            <KnownNote k={known[s.scenario]} />
                          )
                        ) : mayDeclare && projectId ? (
                          <MarkKnown projectId={projectId} scenario={s.scenario} onDone={() => onKnown?.()} />
                        ) : null}
                      </div>
                      {caseLink(s.scenario)}
                    </div>
                  )}
                  {(v === "failed" || v === "error") && evidence[s.scenario] && open !== s.scenario && (
                    <FailureEvidenceView ev={evidence[s.scenario]} runId={runId} message={s.message} />
                  )}
                  {open === s.scenario && (
                    <div className="border-t border-slate-100 dark:border-slate-800">
                      {v !== "failed" && v !== "error" && projectId && (
                        <p className="px-4 pt-2">{caseLink(s.scenario)}</p>
                      )}
                      {groups.plan?.[s.scenario]?.exp && (
                        <p className="bg-slate-50/60 px-4 py-2 text-sm text-slate-600 dark:bg-slate-950/20 dark:text-slate-400">
                          <span className="font-medium text-slate-500">
                            {t("runDetail.shouldShow")}
                          </span>{" "}
                          {groups.plan[s.scenario].exp}
                        </p>
                      )}
                      <Definition def={defs[s.scenario]} scenario={s.scenario} />
                      <Steps steps={steps} runId={runId} def={defs[s.scenario]} recorded={s.steps} />
                    </div>
                  )}
                </div>
              );
            })}
          </Board>
        );
      })}
    </div>
  );
}

/**
 * Step evidence, rendered as what it is rather than as JSON.
 *
 * The record is JSON, but a reader is looking at a shell command, an exit code
 * and some output - and `{"kind":"shell","rc":0,"stderr":"..."}` makes them
 * find those inside punctuation. The shapes are stable and few: `input` is
 * always a single key named after the action, and `result` is `kind` plus
 * either rc/stdout/stderr or status/json. Anything unrecognised still falls
 * back to formatted JSON, so a new action type degrades instead of vanishing.
 */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      {children}
    </div>
  );
}

/** The masked-out rectangles, drawn over whichever image is showing.

    Positioned in PERCENTAGES of the golden's own resolution rather than
    pixels: the same overlay then fits the thumbnail, a phone and the full-size
    image in its own tab, and no code anywhere has to know what the browser
    scaled the picture to.

    Outline and a faint wash, never a solid fill. The BAWU corpus masks 51% of
    the frame - painting that out would hide half the evidence the reader came
    for, and a mask is a statement about the SCORE, not something wrong with
    the screen. aria-hidden because it is decoration: the caption underneath
    says how many areas there are and that they are not scored, which is what a
    screen reader needs. */
function MaskOverlay({
  rects,
  size,
  label,
}: {
  rects: [number, number, number, number][];
  size: [number, number];
  label?: string;
}) {
  const [w, h] = size;
  if (!(w > 0 && h > 0)) return null;
  const pct = (v: number, span: number) => `${(v / span) * 100}%`;
  return (
    <div className="mask-overlay pointer-events-none absolute inset-0" aria-hidden="true">
      {rects.map(([x, y, rw, rh], i) => (
        <div
          key={i}
          title={i18n.t("runDetail.notScored", {
            label: label ?? i18n.t("runDetail.frameFallback"),
            rect: `${x}, ${y}, ${rw}, ${rh}`,
          })}
          className="absolute rounded-[2px] border border-dashed border-sky-500/90 bg-sky-400/15"
          style={{
            left: pct(x, w),
            top: pct(y, h),
            width: pct(rw, w),
            height: pct(rh, h),
          }}
        />
      ))}
    </div>
  );
}

/** The frames one step left, and a way to compare them.

    One image: show it. Several - a comparison leaves the frame, the golden it
    was measured against and their difference - show ONE at a time with a
    switch, plus a blink between the frame and the golden.

    WHY NOT THREE THUMBNAILS SIDE BY SIDE, which is what this was until
    2026-09-07. Two pictures that differ in one word are indistinguishable when
    they sit next to each other at 288 px; on a phone they are not even next to
    each other, they are three screens apart, and the reader is left scrolling
    between them from memory. Swapping the SAME rectangle between two images
    moves the difference and nothing else - the astronomer's blink comparator,
    and the reason `changed_region` and the difference image exist at all.

    Every image stays one click from its own tab, at full size, for anyone who
    wants to look properly.

    THE MASK IS DRAWN ON ALL OF THEM, including while blinking. A masked
    comparison scores part of the screen, and until 2026-09-09 the report said
    so in words only - "masked ... 51% of frame" over a picture with no
    indication of which half. The reader then does the arithmetic that the
    rectangles already answer: a difference they can see may be inside the
    mask and count for nothing, or a pixel outside it may be the entire
    verdict. It matters most on the difference image, which lights up brightly
    in exactly the regions that were excluded, and during the blink, where the
    eye is drawn to the one thing moving - often the ticker nobody is scoring. */
/** `frame`, `golden` and `difference` are decided in StepRec from the file
 *  name and are only ever shown, so they get a word here. Anything else the
 *  harness starts producing shows as it arrived. */
function frameWord(label: string): string {
  return i18n.t(`runDetail.frameLabel.${label}` as "runDetail.frameLabel.frame", {
    defaultValue: label,
  });
}

function Frames({
  frames,
  stepNo,
  mask,
}: {
  frames: { name: string; label: string; url: string }[];
  stepNo?: number | null;
  mask?: { rects: [number, number, number, number][]; size: [number, number] };
}) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(0);
  const [blink, setBlink] = useState(false);
  // On by default: it was asked for, and a mask nobody switched on is a mask
  // nobody knows about. Off is one click, for reading the screen itself.
  const [showMask, setShowMask] = useState(true);
  const a = frames.findIndex((f) => f.label === "frame");
  const b = frames.findIndex((f) => f.label === "golden");
  const canBlink = a >= 0 && b >= 0;
  const rects = mask?.rects ?? [];
  const hasMask = rects.length > 0 && !!mask;

  useEffect(() => {
    if (!blink || !canBlink) return;
    // 600 ms: fast enough that the eye holds the first image while the second
    // arrives, slow enough to read a word in each.
    const t = setInterval(() => setShown((s) => (s === a ? b : a)), 600);
    return () => clearInterval(t);
  }, [blink, canBlink, a, b]);

  const cur = frames[Math.min(shown, frames.length - 1)];
  return (
    <div>
      {(frames.length > 1 || hasMask) && (
        <div className="mb-1 flex flex-wrap items-center gap-1">
          {frames.map((f, i) => (
            <button
              key={f.name}
              onClick={() => {
                setBlink(false);
                setShown(i);
              }}
              aria-pressed={!blink && i === shown}
              className={`rounded px-2 py-0.5 text-[11px] ${
                !blink && i === shown
                  ? "bg-slate-700 text-white dark:bg-slate-200 dark:text-slate-900"
                  : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              {frameWord(f.label)}
            </button>
          ))}
          {canBlink && (
            <button
              onClick={() => setBlink(!blink)}
              aria-pressed={blink}
              title={t("runDetail.blinkTitle")}
              className={`rounded px-2 py-0.5 text-[11px] ${
                blink
                  ? "bg-amber-500 text-white"
                  : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              {t("runDetail.blink")}
            </button>
          )}
          {hasMask && (
            <button
              onClick={() => setShowMask(!showMask)}
              aria-pressed={showMask}
              title={t("runDetail.maskTitle")}
              className={`rounded px-2 py-0.5 text-[11px] ${
                showMask
                  ? "bg-sky-600 text-white"
                  : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              {t("runDetail.mask")}
            </button>
          )}
        </div>
      )}
      <div className="text-xs text-slate-500">
        {frameWord(cur.label)}
        {blink && t("runDetail.blinking")}
        {hasMask &&
          showMask &&
          t("runDetail.maskedAreas", { count: rects.length })}
      </div>
      {/* The frame is the evidence for anything visual, and a link to it is
          not: nobody clicks through 40 links to find the one showing a black
          screen.

          The overlay sits INSIDE the link and over the image, in a wrapper
          that shrinks to the image: the rectangles are a percentage of the
          picture, so they follow it through max-h-72, a phone's width and the
          full-size tab without anybody computing a scale. */}
      <a
        href={cur.url}
        target="_blank"
        rel="noreferrer noopener"
        className="relative mt-0.5 inline-block"
      >
        <img
          src={cur.url}
          alt={t("runDetail.frameAlt", {
            label: frameWord(cur.label),
            step: stepNo ?? "",
          })}
          loading="lazy"
          className="block max-h-72 rounded border border-slate-200 dark:border-slate-700"
        />
        {hasMask && showMask && (
          <MaskOverlay rects={rects} size={mask!.size} label={cur.label} />
        )}
      </a>
    </div>
  );
}

/** The images a step left, labelled. Basename only: the record stores
 *  "shots/NAME.png" and the route serves the frame from the run's own
 *  directory, so passing the path through would ask for a file inside a
 *  directory inside the run's directory.
 *
 *  A step can leave more than one image. compare_golden leaves three - the
 *  frame, the golden it was measured against and their difference - and a
 *  miss is adjudicated by looking at them together; showing only the frame
 *  asks the reader to go and find the goldens directory, which is how a
 *  disputed verdict turns into nobody looking. */
export function framesOf(paths: unknown[], runId: number) {
  const names: string[] = [];
  for (const v of paths) {
    if (typeof v !== "string" || !v) continue;
    const n = v.split("/").pop();
    if (n && !names.includes(n)) names.push(n);
  }
  return names.map((n) => ({
    name: n,
    label: n.startsWith("golden-ref-")
      ? "golden"
      : n.startsWith("golden-diff-")
      ? "difference"
      : "frame",
    url: assetUrl(`/api/v1/runs/${runId}/shots/${encodeURIComponent(n)}`),
  }));
}

/** What a comparison left out of its score, ready to draw over the frames.
 *
 *  `mask_rects` is the resolved geometry and arrives from 2026-09-09 on.
 *  RUNS RECORDED BEFORE THAT still get their overlay: `masked` already
 *  carried the rectangles whenever the scenario wrote them inline, which is
 *  every BAWU comparison in the corpus, and the entries that are region NAMES
 *  are simply not drawable here - the sidecar that defines them lives in the
 *  product's directory, not in the run. Falling back beats showing nothing on
 *  the runs people are actually looking at today. */
export function maskOf(
  raw: unknown,
): { rects: [number, number, number, number][]; size: [number, number] } | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const body = raw as Record<string, unknown>;
  const isRect = (v: unknown): v is [number, number, number, number] =>
    Array.isArray(v) && v.length === 4 && v.every((n) => typeof n === "number");
  const source = Array.isArray(body.mask_rects)
    ? body.mask_rects
    : Array.isArray(body.masked)
      ? body.masked
      : [];
  const rects = source.filter(isRect);
  const size = body.frame_resolution;
  // No resolution, no scale, no honest overlay: a rectangle drawn against a
  // guessed size points at the wrong part of the screen, which is worse than
  // drawing nothing at all.
  const sized =
    Array.isArray(size) &&
    size.length === 2 &&
    size.every((n) => typeof n === "number" && n > 0);
  if (!rects.length || !sized) return undefined;
  return { rects, size: size as [number, number] };
}

/** Half the screen out of the score is worth saying before anyone looks. */
const MASKED_MUCH = 0.25;

/** The failed step's picture and score, on the failure row itself.
 *
 *  The row used to carry the assertion text alone. For a picture comparison
 *  that text is "Exit code 1 instead of 0: None", and the frame, the golden,
 *  their difference and the score sat two clicks and a screen of the step's
 *  input away - run48 on displayos-headless, where the verdict turns on
 *  0.999481 against 0.9995 and on half the screen being masked. For a picture
 *  the picture IS the finding, so it is shown where the finding is. */
export function FailureEvidenceView({
  ev,
  runId,
  message,
}: {
  ev: FailureEvidence;
  runId: number;
  /** The verdict's own message; when there is one, `Why` already said it. */
  message?: string;
}) {
  const { t } = useTranslation();
  const g = ev.golden;
  const frames = framesOf(ev.shots, runId);
  // A step that raised, in a run whose results never came: nothing else on
  // the row says why the case stopped.
  const raised = !g && !(message ?? "").trim() && ev.error ? ev.error : "";
  if (!g && frames.length === 0 && !raised) return null;
  const sim = typeof g?.similarity === "number" ? g.similarity : null;
  const thr = typeof g?.threshold === "number" ? g.threshold : null;
  const masked = typeof g?.masked_fraction === "number" ? g.masked_fraction : null;
  return (
    <div className="-mt-1 space-y-1 px-4 pb-3 text-sm" data-testid="failure-evidence">
      {raised && (
        <Finding>
          <p className="break-words leading-snug">
            {t("runDetail.stepRaised", { step: ev.step_no ?? "?", action: ev.action ?? "" })}{" "}
            <span className="font-mono">{clip(maskSecrets(raised), 300)}</span>
          </p>
        </Finding>
      )}
      {g && sim !== null && thr !== null && g.pass === false && (
        <p className="text-red-800 dark:text-red-300">
          {t("runDetail.goldenMissed", {
            score: score(sim),
            threshold: score(thr),
            gap: score(Math.max(0, thr - sim)),
          })}
        </p>
      )}
      {masked !== null && masked >= MASKED_MUCH && (
        <p className="text-amber-700 dark:text-amber-400">
          {t("runDetail.goldenMaskedMuch", { pct: Math.round(masked * 100) })}
        </p>
      )}
      {g && <GoldenEnvironment body={g} />}
      {frames.length > 0 && (
        <Frames frames={frames} stepNo={ev.step_no} mask={maskOf(g)} />
      )}
    </div>
  );
}

function StepInput({ action, input }: { action: string; input: unknown }) {
  if (input === null || input === undefined) return null;
  // Always {<action>: value} in practice; unwrapped so the label is not
  // repeated as a key inside its own box.
  let v: unknown = input;
  if (typeof input === "object" && !Array.isArray(input)) {
    const keys = Object.keys(input as object);
    if (keys.length === 1 && (keys[0] === action || keys.length === 1)) {
      v = (input as Record<string, unknown>)[keys[0]];
    }
  }
  if (typeof v === "string") return <Field label={action}><Mono>{v}</Mono></Field>;
  if (v && typeof v === "object" && !Array.isArray(v)) {
    return <Field label={action}><Rows obj={v as Record<string, unknown>} /></Field>;
  }
  return <Field label={action}><Mono>{scalarOrJson(v)}</Mono></Field>;
}

/** A compare_golden result, which is a verdict about a picture and reads badly
 *  as key/value rows.
 *
 *  Four things a reader needs and a flat dump buries: whether the score cleared
 *  the threshold, WHERE the threshold came from (0.97 because nobody chose one
 *  is not the same claim as 0.999 measured on this rig), which regions were
 *  scored on their own, and how much of the frame was masked out of the score.
 *  The images sit below, rendered by the step itself. */
type GoldenRegion = { name?: string; similarity?: number; threshold?: number; pass?: boolean };

function isComparison(b: unknown): b is Record<string, unknown> {
  return (
    !!b && typeof b === "object" && !Array.isArray(b) &&
    "golden" in (b as Record<string, unknown>) &&
    "similarity" in (b as Record<string, unknown>)
  );
}

function score(v: unknown, digits = 5): string {
  return typeof v === "number"
    ? v.toFixed(digits)
    : String(v ?? i18n.t("common.dash"));
}

function GoldenResult({
  body,
  expected,
}: {
  body: Record<string, unknown>;
  expected?: boolean;
}) {
  const regions: GoldenRegion[] = Array.isArray(body.regions)
    ? (body.regions as GoldenRegion[])
    : [];
  const masked = Array.isArray(body.masked) ? (body.masked as unknown[]) : [];
  const prov =
    body.golden_provenance && typeof body.golden_provenance === "object" &&
    !Array.isArray(body.golden_provenance)
      ? (body.golden_provenance as Record<string, unknown>)
      : null;
  const belowNoise = body.threshold_below_noise === true;
  const t = i18n.t.bind(i18n);

  return (
    <div className="mt-0.5 space-y-1">
      <div className="flex flex-wrap items-center gap-1">
        {/* Red means "this step did not do what it claimed", not "the pictures
            differ". A scenario whose whole job is to prove the oracle CAN fail
            - DOS-PHASE-DISTINCT compares a departing screen against the
            at-stop golden and asserts the miss - was painted as a failure
            while all three of its assertions passed underneath, which is the
            page crying wolf on its own evidence. */}
        <Chip ok={body.pass === true ? true : expected ? undefined : false}>
          {score(body.similarity)} / {score(body.threshold)}
        </Chip>
        {body.pass === false && expected && <Chip>{t("runDetail.theMiss")}</Chip>}
        <span className="break-all font-mono text-[11px] text-slate-500">
          {String(body.golden)}
        </span>
        {body.recorded === true && <Chip>{t("runDetail.recorded")}</Chip>}
        {body.threshold_source !== undefined && (
          <Chip>
            {t("runDetail.thresholdSource", {
              source: String(body.threshold_source),
            })}
          </Chip>
        )}
        {masked.length > 0 && (
          <Chip>
            {t("runDetail.masked", { list: masked.map(String).join(", ") })}
            {typeof body.masked_fraction === "number"
              ? t("runDetail.maskedFraction", {
                  pct: (body.masked_fraction * 100).toFixed(1),
                })
              : ""}
          </Chip>
        )}
        {typeof body.self_similarity === "number" && (
          <Chip ok={belowNoise ? false : undefined}>
            {t("runDetail.stillScreenFloor", {
              score: score(body.self_similarity),
            })}
          </Chip>
        )}
      </div>

      {belowNoise && (
        /* The one case where a red verdict is not about the product. */
        <div className="text-[11px] text-amber-700 dark:text-amber-400">
          {t("runDetail.belowNoise")}
        </div>
      )}
      <GoldenEnvironment body={body} />

      {regions.length > 0 && (
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-slate-400">
              <th className="text-left font-normal">{t("runDetail.colRegion")}</th>
              <th className="text-right font-normal">{t("runDetail.colScore")}</th>
              <th className="text-right font-normal">
                {t("runDetail.colThreshold")}
              </th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {regions.map((r, i) => (
              <tr
                key={r.name ?? i}
                className={
                  r.pass === false ? "text-red-700 dark:text-red-400" : undefined
                }
              >
                <td className="truncate">{r.name ?? t("common.dash")}</td>
                <td className="text-right tabular-nums">{score(r.similarity)}</td>
                <td className="text-right tabular-nums text-slate-400">
                  {score(r.threshold)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {prov && (
        <div>
          <div className="text-xs text-slate-500">
            {t("runDetail.goldenRecordedFrom")}
          </div>
          <Rows obj={prov} />
        </div>
      )}
    </div>
  );
}

function StepResult({ result, expected }: { result: unknown; expected?: boolean }) {
  if (result === null || result === undefined) return null;
  if (typeof result !== "object" || Array.isArray(result)) {
    return (
      <Field label={i18n.t("runDetail.returned")}>
        <Mono>{scalarOrJson(result)}</Mono>
      </Field>
    );
  }
  const r = result as Record<string, unknown>;
  // `kind` only ever repeats the action.
  const { kind: _kind, rc, status, stdout, stderr, json: body, png_bytes, ...rest } = r;
  const chips: React.ReactNode[] = [];
  // `expected` is "every assertion on this step passed". A non-zero rc or a
  // 4xx that the scenario ASKED for is a fact, not a fault, so it reads
  // neutral; one nobody asserted anything about stays red, because then
  // nothing else on the page is going to mention it.
  const tone = (good: boolean) => (good ? true : expected ? undefined : false);
  if (rc !== undefined)
    chips.push(<Chip key="rc" ok={tone(rc === 0)}>rc {String(rc)}</Chip>);
  if (status !== undefined)
    chips.push(
      <Chip key="st" ok={tone(Number(status) >= 200 && Number(status) < 400)}>
        HTTP {String(status)}
      </Chip>,
    );
  if (png_bytes !== undefined)
    chips.push(<Chip key="png">PNG {String(png_bytes)} bytes</Chip>);

  const extra = Object.keys(rest).length ? rest : null;
  if (!chips.length && !stdout && !stderr && body === undefined && !extra) return null;

  return (
    <Field label={i18n.t("runDetail.returned")}>
      {chips.length > 0 && <div className="mt-0.5 flex flex-wrap gap-1">{chips}</div>}
      {typeof stdout === "string" && stdout.trim() !== "" && (
        <div className="mt-1">
          <div className="text-xs text-slate-500">
            {i18n.t("runDetail.stdout")}
          </div>
          <Mono>{stdout}</Mono>
        </div>
      )}
      {typeof stderr === "string" && stderr.trim() !== "" && (
        <div className="mt-1">
          <div className="text-xs text-slate-500">
            {i18n.t("runDetail.stderr")}
          </div>
          <Mono>{stderr}</Mono>
        </div>
      )}
      {body !== undefined &&
        (isScene(body) ? (
          <SceneResult body={body} expected={expected} />
        ) : isComparison(body) ? (
          <GoldenResult body={body} expected={expected} />
        ) : body && typeof body === "object" && !Array.isArray(body) ? (
          <Rows obj={body as Record<string, unknown>} />
        ) : (
          <Mono>{scalarOrJson(body)}</Mono>
        ))}
      {extra && <Rows obj={extra} />}
    </Field>
  );
}


type Check = { assert?: unknown; ok?: boolean; why?: string };
type Rec = {
  step?: number | null;
  total?: number | null;
  action?: string | null;
  input?: unknown;
  result?: unknown;
  checks?: Check[];
  shot?: string | null;
  /** Every image the step left, primary first. Present only when an action
      produced more than one: a compare_golden miss adds the golden it was
      measured against and their difference. */
  shots?: string[] | null;
  ok?: boolean | null;
  secs?: number | null;
  /** Written when a step BEGINS; the completed record follows it. */
  started?: boolean;
  // Records that are not steps: the readiness barrier, the baseline capture and
  // the restore block. None of them carries a step number.
  phase?: string | null;
  errors?: unknown[];
  count?: number | null;
  saved?: unknown;
};

/**
 * One step, with the evidence under it.
 *
 * The engine's record is the source of truth and it already holds everything a
 * reader needs: the command that ran, what came back, and every assertion with
 * its own verdict. Showing only the action name - which is what this did at
 * first - leaves "shell" on the screen and the finding in the database, so a
 * failure could be seen but not diagnosed, which is the entire job of the page.
 */
/**
 * What the bench is doing right now.
 *
 * The tiles below say what a run HAS done; while it is going, the question is
 * what it is doing, and the answer was only visible by opening the right
 * scenario and guessing which step was last. Derived from the newest step
 * record - the highest seq IS the newest thing the bench has said, so there is
 * no second source of liveness to go stale.
 */
function NowRunning({ st }: { st: Step }) {
  const { t } = useTranslation();
  let d: Rec = {};
  try {
    d = JSON.parse(st.raw_json) as Rec;
  } catch {
    d = {};
  }
  const inner =
    d.input && typeof d.input === "object" && !Array.isArray(d.input)
      ? Object.values(d.input as Record<string, unknown>)[0]
      : d.input;
  const cmd = (typeof inner === "string" ? inner : scalarOrJson(inner, 160))
    .replace(/\s+/g, " ")
    .trim();
  const pct = d.total && st.step_no ? Math.round((100 * st.step_no) / d.total) : null;

  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-sky-300 bg-sky-50 px-4 py-3 dark:border-sky-900 dark:bg-sky-950/40">
      <div className="flex flex-wrap items-baseline gap-2 text-sm">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-500" />
        </span>
        <span className="font-semibold text-sky-900 dark:text-sky-200">
          {t("runDetail.nowRunning")}
        </span>
        <span className="font-mono font-medium">{st.scenario}</span>
        <span className="text-sky-900/70 dark:text-sky-200/70">
          {d.total
            ? t("live.stepOf", {
                no: st.step_no ?? t("live.unknownStep"),
                total: d.total,
              })
            : t("live.step", { no: st.step_no ?? t("live.unknownStep") })}{" "}
          · {st.action || t("common.dash")}
        </span>
        <span className="ml-auto text-xs text-sky-900/60 dark:text-sky-200/60">
          {ago(st.ts ?? 0)}
        </span>
      </div>
      {pct !== null && (
        <div className="mt-2 h-1 overflow-hidden rounded bg-sky-200 dark:bg-sky-900">
          <div className="h-full bg-sky-500" style={{ width: `${pct}%` }} />
        </div>
      )}
      {cmd && (
        <div className="mt-1 truncate font-mono text-xs text-sky-900/70 dark:text-sky-200/70">
          {cmd}
        </div>
      )}
    </div>
  );
}

/**
 * The harness's own gate, reported APART from the product's numbers.
 *
 * A harness that fails its own tests cannot make a trustworthy claim about the
 * device. Folding this into the run's pass/fail would either put a harness
 * problem on the product's scoreboard or hide it there; both are worse than a
 * separate line that says which one broke.
 *
 * Silent when it passed - which is almost always - because a green gate is not
 * news, and a permanent banner is a banner nobody reads.
 */
function SelfTest({ st }: { st: { ok?: boolean; summary?: string; files?: string } }) {
  const { t } = useTranslation();
  if (st.ok === undefined) return null;
  if (st.ok) {
    return (
      <p className="mt-3 text-xs text-slate-500">
        {st.summary
          ? t("runDetail.selfTestPassedWith", { summary: st.summary })
          : t("runDetail.selfTestPassed")}
      </p>
    );
  }
  return (
    <Notice tone="error">
      <Trans i18nKey="runDetail.selfTestFailed" components={{ b: <b /> }} />
      {st.summary && <div className="mt-1 font-mono text-xs">{st.summary}</div>}
    </Notice>
  );
}

/** The run's own output: long, and the last resort when the step record does
    not explain something.

    `bare` is for the tab, where a disclosure inside a panel somebody chose to
    open is a second door to the same room. Without it the disclosure stays,
    for anyone rendering this under the page rather than in a tab. */
function RunLog({ text, bare }: { text: string; bare?: boolean }) {
  const { t } = useTranslation();
  if (!text.trim()) return null;
  const body = (
    <pre className="mt-2 max-h-[32rem] overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] leading-relaxed dark:border-slate-800 dark:bg-slate-900">
      {text}
    </pre>
  );
  if (bare) {
    return (
      <section className="mt-4">
        <p className="text-xs text-slate-500">
          {t("runDetail.logSize", { kb: Math.round(text.length / 1024) })}
        </p>
        {body}
      </section>
    );
  }
  return (
    <details className="mt-6">
      <summary className="cursor-pointer text-base font-semibold text-slate-800 dark:text-slate-200">
        {t("runDetail.runLog", { kb: Math.round(text.length / 1024) })}
      </summary>
      {body}
    </details>
  );
}

/**
 * What changed since the previous run on this bench.
 *
 * The comparison nobody makes from two tabs, and the one that decides whether a
 * report needs acting on: twelve failures are routine if they were all failing
 * yesterday, and one is an emergency if it passed. Newly-broken comes first
 * because it is the only part that is ever urgent.
 *
 * Silent when nothing moved, which on a stable suite is most days.
 */
/** Why the results are not compared with an earlier run (piece 5).

    Said instead of comparing unlike runs: a changed result against another
    profile, or against the same profile on other test data, would read as a
    change in the product when it is not one. */
export function NotCompared({ note }: { note?: { why: string; run_id?: number; run?: string } | null }) {
  const { t } = useTranslation();
  if (!note) return null;
  return (
    <p
      className="mt-4 rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300"
      data-testid="not-compared"
    >
      {note.why === "test_data_changed" && note.run_id ? (
        <Trans
          i18nKey="runDetail.notComparedData"
          values={{ run: note.run }}
          components={{ runLink: <Link className="underline" to={`/runs/${note.run_id}`} /> }}
        />
      ) : note.why === "no_test_data" ? (
        t("runDetail.notComparedNoData")
      ) : (
        t("runDetail.notComparedNoProfile")
      )}
    </p>
  );
}

function Changed({
  t,
  against,
}: {
  t: {
    previous: { id: number; name: string };
    broke: { scenario: string; was: string; now: string }[];
    fixed: { scenario: string; was: string; now: string }[];
    compared: number;
    only_then: string[];
  } | null;
  /** What the comparison is FOR, when it is not the default one. The two
      answer different questions and are worth nothing if a reader takes one
      for the other: "since the run before this on this rig" mixes a new build
      with a new corpus and a new day, while "same layout, other build" holds
      everything but the product still. */
  against?: React.ReactNode;
}) {
  const { t: tr } = useTranslation();
  if (!t) return null;
  const nothing = t.broke.length === 0 && t.fixed.length === 0;
  return (
    <div className="mt-4 rounded-md border border-slate-200 bg-white px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="text-sm text-slate-500">
        {against ?? tr("runDetail.since")}{" "}
        <Link className="underline" to={`/runs/${t.previous.id}`}>
          {t.previous.name}
        </Link>{" "}
        {tr("runDetail.compared", { count: t.compared })}
      </div>
      {nothing ? (
        <p className="mt-1 text-slate-500">
          {tr("runDetail.nothingChanged")}
          {t.only_then.length > 0 &&
            tr("runDetail.onlyThen", { count: t.only_then.length })}
        </p>
      ) : (
        <div className="mt-1 space-y-1">
          {t.broke.length > 0 && (
            <div>
              <span className="font-semibold text-red-700 dark:text-red-400">
                {tr("runDetail.newlyFailing", { count: t.broke.length })}
              </span>
              <span className="ml-2 font-mono text-xs">
                {t.broke.map((x) => x.scenario).join(", ")}
              </span>
            </div>
          )}
          {t.fixed.length > 0 && (
            <div>
              <span className="font-semibold text-success-700 dark:text-success-400">
                {tr("runDetail.newlyPassing", { count: t.fixed.length })}
              </span>
              <span className="ml-2 font-mono text-xs">
                {t.fixed.map((x) => x.scenario).join(", ")}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** What each bench-built level contains. The LEVEL names are what the harness
 *  wrote and stay as they are; only the sentence beside them is translated,
 *  and a level nobody has a sentence for shows its own name. */
function pdfHelp(level: string): string {
  return i18n.t(`runDetail.pdfHelp.${level}` as "runDetail.pdfHelp.full", {
    defaultValue: level,
  });
}

/**
 * The report as a document, at the level the reader actually needs.
 *
 * Five stops rather than one "download": the person asking is either reporting
 * a verdict upward, chasing one failure, or attaching evidence to a ticket, and
 * those want very different documents - the ends differ by about 4x in size and
 * build time. Built by the harness on the bench, which is where the generator
 * everybody's filed reports came from already lives.
 *
 * Only the levels that EXIST are offered. A button that looks like the others
 * and 404s is worse than no button.
 */
export function Reports({ runId, pdfs }: { runId: number; pdfs: { level: string; bytes: number }[] }) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<Report[]>([]);
  useEffect(() => {
    let alive = true;
    api.reports()
      .then((d) => alive && setTemplates(d.reports))
      .catch(() => { /* the run page must still render without the report list */ });
    return () => { alive = false; };
  }, []);

  return (
    <section className="mt-6">
      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">
        {t("runDetail.reportHeading")}
      </h2>

      {/* Rendered HERE, from central's own tables, about THIS run. The five
          fixed levels the bench used to build are now definitions somebody can
          edit and reorder - `standard` is no longer a boolean buried in
          pdfreport.py. Each link pins the template to this run, so one
          definition serves every run rather than one document per run. */}
      <div className="mt-2 flex flex-wrap gap-2">
        {templates.map((r) => (
          <span key={r.id}
                className="inline-flex overflow-hidden rounded border border-slate-300 text-sm dark:border-slate-700">
            <a
              href={assetUrl(`/api/v1/reports/${r.id}/render?run=${runId}`)}
              target="_blank" rel="noreferrer noopener"
              title={r.description || r.name}
              className="px-3 py-1.5 hover:bg-slate-50 hover:text-sky-700 dark:hover:bg-slate-800"
            >
              {r.name}
            </a>
            <a
              href={assetUrl(`/api/v1/reports/${r.id}/render?run=${runId}&format=pdf`)}
              target="_blank" rel="noreferrer noopener"
              title={t("runDetail.asPdf", { name: r.name })}
              className="border-l border-slate-300 px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-50 hover:text-sky-700 dark:border-slate-700 dark:hover:bg-slate-800"
            >
              PDF
            </a>
          </span>
        ))}
        {templates.length === 0 && (
          <span className="text-sm text-slate-500">
            <Trans
              i18nKey="runDetail.noTemplates"
              components={{ link: <Link className="underline" to="/reports" /> }}
            />
          </span>
        )}
      </div>
      <p className="mt-1.5 text-xs text-slate-500">
        <Trans
          i18nKey="runDetail.reportNote"
          components={{ link: <Link className="underline" to="/reports" /> }}
        />
      </p>

      {/* The bench-built files, kept and labelled as what they are. They are
          the artefact the run itself produced, so they stay as evidence - but
          they are no longer the thing to open, and a page that offered both
          without saying which is which would get the wrong one quoted. */}
      {pdfs.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-slate-500">
            {t("runDetail.archive", { count: pdfs.length })}
          </summary>
          <div className="mt-2 flex flex-wrap gap-2">
            {pdfs.map((p) => (
              <a
                key={p.level}
                href={assetUrl(`/api/v1/runs/${runId}/pdf/${p.level}`)}
                target="_blank"
                rel="noreferrer noopener"
                title={pdfHelp(p.level)}
                className="rounded border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:border-sky-400 dark:border-slate-800 dark:text-slate-300"
              >
                {p.level}
                <span className="ml-2 text-slate-400">
                  {p.bytes > 1024 * 1024
                    ? t("runDetail.mb", { n: (p.bytes / 1024 / 1024).toFixed(1) })
                    : t("runDetail.kb", {
                        n: Math.max(1, Math.round(p.bytes / 1024)),
                      })}
                </span>
              </a>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

/** Past this, a step's input is folded: the one-line summary on the row
 *  already says what it was asked. */
const LONG_INPUT = 300;

export function StepRec({ st, runId, why }: { st: Step; runId?: number; why?: Prose[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  let d: Rec = {};
  try {
    d = JSON.parse(st.raw_json) as Rec;
  } catch {
    d = {};
  }

  // Not a step: the setup barrier, or the restore block at the end. Rendered
  // rather than skipped - a scenario that passed every step and then failed its
  // cleanup is exactly the case a step-only view is silent about.
  if (d.phase) {
    const label =
      d.phase === "restore"
        ? t("runDetail.phaseCleanup")
        : d.phase === "setup"
          ? t("runDetail.phaseSetup")
          : d.phase;
    return (
      <li className="min-w-0 text-xs">
        <span className="font-semibold text-slate-500">
          {label}
        </span>{" "}
        {d.ok ? (
          <span className="text-success-700 dark:text-success-400">
            {d.count
              ? t("runDetail.phasePassedRestored", { count: d.count })
              : t("runDetail.phasePassed")}
          </span>
        ) : (
          <span className="text-red-700 dark:text-red-400">
            {t("runDetail.phaseFailed")}
          </span>
        )}
        {(d.errors || []).map((e, i) => (
          <div key={i} className="mt-0.5 break-words text-red-700 dark:text-red-400">
            {String(e)}
          </div>
        ))}
      </li>
    );
  }

  // One line: `before = false`. A two-column table for one pair set the value
  // a screen's width away from its name.
  if (d.saved !== undefined) {
    const saved =
      d.saved && typeof d.saved === "object" && !Array.isArray(d.saved)
        ? (d.saved as Record<string, unknown>)
        : null;
    return (
      <li className="min-w-0 text-xs">
        <span className="font-semibold text-slate-500">
          {t("runDetail.saved")}
        </span>{" "}
        {saved ? (
          Object.entries(saved).map(([k, v]) => {
            const shown = scalarOrJson(v, 2000);
            return (
              <span key={k} className="mr-3 inline-block break-all font-mono">
                <span className="text-slate-500">{k}</span>
                {" = "}
                <span
                  className="text-slate-800 dark:text-slate-200"
                  title={shown.length > 80 ? shown : undefined}
                >
                  {clip(shown, 80)}
                </span>
              </span>
            );
          })
        ) : (
          <Mono>{scalarOrJson(d.saved, 600)}</Mono>
        )}
      </li>
    );
  }

  const checks = d.checks || [];
  const failed = checks.filter((c) => c.ok === false);
  // One line for the collapsed row: the command itself, unwrapped from its
  // single-key envelope, so two `shell` steps are told apart at a glance.
  const inner =
    d.input && typeof d.input === "object" && !Array.isArray(d.input)
      ? Object.values(d.input as Record<string, unknown>)[0]
      : d.input;
  // What the step was asked, in words rather than JSON: `CTRL+I`, `POST
  // /api/x · N2`, `kvm_key NUMLOCK · until num_lock ≠ $before · up to 12 s`.
  // An empty input shows nothing.
  const cmd = formatInput(st.action, inner).replace(/\s+/g, " ").trim();
  // Basename only: the record stores "shots/NAME.png" and the route serves the
  // frame from the run's own directory, so passing the path through would ask
  // for a file inside a directory inside the run's directory.
  //
  // A step can leave more than one image. compare_golden leaves three - the
  // frame, the golden it was measured against and their difference - and a
  // miss is adjudicated by looking at them together; showing only the frame
  // asks the reader to go and find the goldens directory, which is how a
  // disputed verdict turns into nobody looking.
  const frames = runId ? framesOf([d.shot, ...(Array.isArray(d.shots) ? d.shots : [])], runId) : [];

  // What the comparison left out of its score, ready to draw over the frames.
  const mask = maskOf(
    d.result && typeof d.result === "object"
      ? (d.result as Record<string, unknown>).json
      : undefined,
  );

  return (
    <li className="min-w-0 text-xs">
      <button
        className="flex w-full min-w-0 items-baseline gap-2 overflow-hidden text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span
          className={`mt-0.5 inline-block h-4 w-4 shrink-0 rounded-full text-center text-[10px] font-semibold leading-4 ${
            st.ok === 0
              ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
          }`}
        >
          {st.step_no ?? "·"}
        </span>
        {/* One line, always. A long shell command must truncate rather than
            widen the row - an overflowing row scrolls the whole page sideways
            on a phone, which is where this gets read. min-w-0 is what lets a
            flex child shrink below its content. */}
        <span className="flex min-w-0 flex-1 items-baseline gap-2 overflow-hidden">
          <span className="shrink-0 font-mono text-slate-700 dark:text-slate-300">
            {st.action || t("common.dash")}
          </span>
          {d.secs != null && (
            <span className="shrink-0 tabular-nums text-slate-400">
              {d.secs.toFixed(2)}s
            </span>
          )}
          {/* What distinguishes one `shell` step from the next: needing a click
              to tell them apart makes a long scenario unreadable. */}
          {cmd && (
            <span className="min-w-0 flex-1 truncate font-mono text-slate-400">
              {cmd}
            </span>
          )}
          {!d.result && d.started && (
            <span className="shrink-0 text-amber-600 dark:text-amber-400">
              {t("runDetail.runningEllipsis")}
            </span>
          )}
        </span>
        <span className="shrink-0 text-slate-400">{open ? "−" : "+"}</span>
      </button>
      {/* A failed assertion is the finding, so it shows without a click. */}
      {failed.map((c, i) => (
        <div key={i} className="ml-6 mt-0.5 break-words text-red-700 dark:text-red-400">
          {c.why ? <ReasonText reason={parseReason(maskSecrets(c.why))} /> : formatAssert(c.assert)}
        </div>
      ))}

      {/* Two lines of the step's reason, the whole of it once the row is
          open. Below the failure, not above it: the finding first, then what
          the step was for (sncf-d6). ml-6 is the badge and its gap, so the
          text starts under the action it explains. */}
      {why && (
        <p
          className={`ml-6 mt-0.5 break-words text-slate-500 dark:text-slate-400 ${open ? "" : "line-clamp-2"}`}
          data-testid="step-why"
        >
          {why.map((p) => p.join(" ")).join(" ")}
        </p>
      )}

      {open && (
        <div className="ml-6 mt-1 space-y-2">
          {/* The pictures before the parameters: a compare_golden's input is
              four mask rectangles and a grab block, which printed one number
              per line pushed the frames a screen below the step (run48). */}
          {frames.length > 0 && (
            <Frames frames={frames} stepNo={st.step_no} mask={mask} />
          )}
          <StepResult
            result={d.result}
            expected={checks.length > 0 && failed.length === 0}
          />
          {JSON.stringify(d.input ?? "").length > LONG_INPUT ? (
            <details>
              <summary className="cursor-pointer text-slate-500">{t("runDetail.stepGiven")}</summary>
              <StepInput action={st.action || t("runDetail.sent")} input={d.input} />
            </details>
          ) : (
            <StepInput action={st.action || t("runDetail.sent")} input={d.input} />
          )}
          {checks.length > 0 ? (
            <div className="space-y-0.5">
              {checks.map((c, i) => (
                <div
                  key={i}
                  className={
                    c.ok === false
                      ? "text-red-700 dark:text-red-400"
                      : "text-success-700 dark:text-success-400"
                  }
                >
                  {c.ok === false ? t("runDetail.fail") : t("runDetail.pass")}{" "}
                  <span className="font-mono">{formatAssert(c.assert)}</span>
                  {/* The sentence is above, on the row; the harness's own
                      words are here, whole, for whoever needs to grep them. */}
                  {c.ok === false && c.why && (
                    <details className="text-red-700 dark:text-red-400">
                      <summary className="cursor-pointer text-[11px] opacity-80">
                        {t("human.raw")}
                      </summary>
                      <Mono>{maskSecrets(c.why)}</Mono>
                    </details>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-slate-400">{t("runDetail.noAssertion")}</div>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Drop the "started" half of each step.
 *
 * The engine writes a record when a step BEGINS and another when it ends, so a
 * live view can say "now running step 8". Once the second one exists the first
 * carries nothing the second does not - and showing both listed every step
 * twice, which is what the page did.
 *
 * A started record with nothing after it is KEPT: that is a step still running,
 * or the one the run died in, and it is the most interesting row on the page.
 */
export function mergeStarted(steps: Step[]): Step[] {
  const lastSeqFor = new Map<string, number>();
  for (const s of steps) {
    const k = `${s.scenario}#${s.step_no}`;
    lastSeqFor.set(k, Math.max(lastSeqFor.get(k) ?? -1, s.seq));
  }
  return steps.filter((s) => {
    let started = false;
    try {
      started = JSON.parse(s.raw_json)?.started === true;
    } catch {
      started = false;
    }
    if (!started) return true;
    return lastSeqFor.get(`${s.scenario}#${s.step_no}`) === s.seq;
  });
}

function Steps({
  steps,
  runId,
  def,
  recorded,
}: {
  steps: Step[];
  runId?: number;
  def?: ScenarioDefinition;
  /** How many steps the run summary says the case has. 0 is a case that ended
      before its first step, or records none: its verdict and message are all
      there is, and "loading" would never end. */
  recorded?: number;
}) {
  // Each executed step beside the comment its scenario gives it, so the reader
  // of a failure sees what the step was FOR without opening the definition.
  // Only where the action agrees: a definition from another text than the one
  // that ran must not lend its reasons to the wrong step.
  const view = useScenarioView(def?.source, def?.verbs);
  const whyOf = (st: Step): Prose[] | undefined => {
    const s = view?.placed && st.step_no ? view.steps[st.step_no - 1] : undefined;
    return s && s.action === st.action && s.why.length ? s.why : undefined;
  };
  if (steps.length === 0) {
    return (
      <p className="px-3 pb-3 text-xs text-slate-500">
        {i18n.t(recorded === 0 ? "runDetail.noSteps" : "common.loading")}
      </p>
    );
  }
  const rows = mergeStarted(steps);
  return (
    <ol className="space-y-2 border-t border-slate-200 px-3 py-3 dark:border-slate-800">
      {rows.map((st) => (
        <StepRec key={st.seq} st={st} runId={runId} why={whyOf(st)} />
      ))}
    </ol>
  );
}
